/**
 * Positive reinforcement screens (PositiveReinforcementController.cs +
 * Prefabs/Player/EndReinforcement/*):
 *
 *  - EndSlideOverlay: shown when a game slide ends. A translucent white veil
 *    fades in at once; two seconds later the stars (SimpleStars1/2/3 when
 *    `prStarsShowScore`, otherwise SimpleStarsAll) or the red "wrong" mark
 *    appear. Tapping anywhere goes on to the next slide.
 *  - EndProjectOverlay: white screen with the smile, and, with
 *    `prStarsShowScore`, three stars and the total stars counting up
 *    (PositiveReinforcementFinalStars.cs). Tapping closes the project.
 *
 * Geometry is the prefabs' (2048×1536 canvas, Unity y-up converted to CSS).
 * Only the "simple" style is ported; "characters" (nen/nena) falls back to it.
 */
import { useEffect, useState, type CSSProperties } from "react";
import { libraryUrl } from "../data/media";
import { fontFamilyFor } from "./fonts";
import { t } from "./i18n";

const ui = (name: string) => libraryUrl(`ui/${name}.png`);

/** Absolute box from a Unity centre-anchored rect (x right, y up, pivot centre). */
function box(x: number, y: number, w: number, h: number, rotateDeg = 0): CSSProperties {
  return {
    position: "absolute",
    left: 1024 + x - w / 2,
    top: 768 - y - h / 2,
    width: w,
    height: h,
    transform: rotateDeg ? `rotate(${rotateDeg}deg)` : undefined,
  };
}

function Sprite({ name, style, className }: { name: string; style: CSSProperties; className?: string }) {
  return <img src={ui(name)} alt="" draggable={false} className={className} style={{ objectFit: "contain", pointerEvents: "none", ...style }} />;
}

/** SimpleStars1 / 2 / 3 / All */
function Stars({ count, scale = 1, originX = 0, originY = 0, decorated = true }: { count: 1 | 2 | 3; scale?: number; originX?: number; originY?: number; decorated?: boolean }) {
  return (
    <div className="pr-stars" style={{ position: "absolute", inset: 0, transform: `translate(${originX}px, ${originY}px) scale(${scale})`, transformOrigin: "1024px 768px" }}>
      {decorated && (
        <>
          <Sprite name="starsBg" style={box(9, 40, 976, 425)} />
          <Sprite name="decoBlue" style={box(-215, 365, 128, 128, 137)} className="pr-deco" />
          <Sprite name="decoBlue" style={box(592, -59, 128, 128, -90)} className="pr-deco" />
          <Sprite name="decoViolet" style={box(-593, -87, 128, 128, -60.5)} className="pr-deco" />
          <Sprite name="decoViolet" style={box(503, 190, 128, 128, -148.8)} className="pr-deco" />
          <Sprite name="decoRed" style={box(274, 280, 64, 64)} className="pr-deco" />
          <Sprite name="dotYellow" style={box(-425, 194, 64, 64)} className="pr-deco" />
          <div style={{ ...box(-6, -311, 900, 80), background: "#7f3f98", borderRadius: 40 }} />
        </>
      )}
      <Sprite name="star" style={box(-336, -38, 300, 300)} className="pr-star pr-star-1" />
      {count >= 2 && <Sprite name="star" style={box(-3, 40, 450, 450)} className="pr-star pr-star-2" />}
      {count >= 3 && <Sprite name="star" style={box(354, -38, 300, 300)} className="pr-star pr-star-3" />}
    </div>
  );
}

export function EndSlideOverlay({ correct, stars, showScore, onContinue }: { correct: boolean; stars: number; showScore: boolean; onContinue: () => void }) {
  const [content, setContent] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setContent(true), 2000);
    return () => window.clearTimeout(t);
  }, []);
  const count = (showScore ? Math.min(3, Math.max(1, stars)) : 3) as 1 | 2 | 3;
  return (
    <div className="pr-overlay" onClick={onContinue} role="button" aria-label={t("continue")}>
      {content && (correct ? <Stars count={count} /> : <Sprite name="wrong" className="pr-wrong" style={box(0, 0, 1036, 1036)} />)}
    </div>
  );
}

export function EndProjectOverlay({ totalStars, showScore, font, onClose }: { totalStars: number; showScore: boolean; font: string | null; onClose: () => void }) {
  const [shown, setShown] = useState(0);
  // PositiveReinforcementFinalStars: count up 10 times per second, ~3 s at most.
  useEffect(() => {
    if (!showScore) return;
    const increment = totalStars > 30 ? Math.floor(totalStars / 30) : 1;
    let n = 0;
    const id = window.setInterval(() => {
      n = Math.min(totalStars, n + increment);
      setShown(n);
      if (n >= totalStars) window.clearInterval(id);
    }, 100);
    return () => window.clearInterval(id);
  }, [totalStars, showScore]);

  return (
    <div className="pr-overlay pr-end" onClick={onClose} role="button" aria-label={t("exit")}>
      {showScore ? (
        <>
          <Sprite name="smile" style={{ position: "absolute", left: 624, top: 100, width: 800, height: 800 }} />
          <Stars count={3} scale={0.7} originY={343} decorated={false} />
          <div className="pr-total" style={{ position: "absolute", left: 0, right: 0, top: 1286, textAlign: "center", fontFamily: `"MakeIt Bold", ${fontFamilyFor(font)}`, fontSize: 196, lineHeight: 1, color: "#7f3f98" }}>
            {shown}
          </div>
        </>
      ) : (
        <Sprite name="smile" style={box(0, 0, 1036, 1036)} />
      )}
    </div>
  );
}
